// @givmo/board — providing a copy of the return to the board (CBG-YR5-DIST's web half, frame p4).
//
// ══ THREE STEPS, AND THE MIDDLE ONE IS NOT OUR SERVER ═══════════════════════════════════════════
//
//   1. ISSUE — a signed PUT URL for one PDF, under a key the server mints (`board-return-
//      distribution.md` §3.2). The officer never chooses where bytes land.
//   2. PUT — the bytes go straight to storage from the browser. ⚠ The signature binds BOTH headers
//      below (`Content-Type` and the baked-in `Content-Disposition: attachment`, the packet
//      documents' signer); omitting one is a 403 that reads like a permissions problem.
//   3. CHECK — Board reads the object itself: the generation it read, its pages, its size and the
//      SHA-256 it computed (never a digest from this browser), refusing a password-protected PDF.
//      Nothing is written. The act ("Provide it") then binds that generation again, server-side.
//
// Who receives it is the recipients' preview (the roster now, directors first): the same people the
// act will freeze. The one email each gets is the backend's; this dialog only says what it says.
//
// ⚠ Board reads nothing out of the file and shows it exactly as added. The IRS's own words about what
// line 11a asks for are one click away, quoted from the Form 990 read (D-CBG-30), never typed here.

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, ErrorMessage, Icon, Skeleton } from '@givmo/design-system';
import { FileText } from 'lucide-react';
import { useBoardOperations } from '../../board-runtime';
import { useNavigate } from '../../board-routing';
import { todayAsDateInput } from '../../lib/display';
import { DialogFrame, PartLabel } from '../conflicts/coi-parts';
import { WhatTheInstructionsSay } from '../form990/form990-evidence';
import type { Form990Instructions } from '../form990/form990-decode';
import { decodeCopyCheck, fileSize, type CopyCheck, type ReturnRecipient } from './return-decode';
import { servedSentence, useReturnRecipients, useSettleReturnAct } from './return-queries';
import { returnWords as w } from './return-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const PDF = 'application/pdf';

/** A refusal this dialog words itself (the file's size, a failed PUT): shown as it is. */
class SaidError extends Error {}

export interface ProvideTarget {
  /** The tax year the copy is OF, by its dates and its named year (the Form 990 page's year). */
  taxYear: { begins_on: string; ends_on: string; named_year: number };
  /** The form, as the copy's title names it ("990"). */
  form: string;
  /**
   * The month and day the tax years end on, when the organization recorded none in Givmo: the Form
   * 990 page's chosen day, or (a newer copy) the copy's own last day. With a recorded year end the
   * backend reads its own and ignores this.
   */
  yearEnd: { month: number; day: number } | null;
  /** The copy this one follows ("Provide a newer copy"), or `null` for the year's first. */
  followsRecordId: string | null;
  /** Line 11a's instruction excerpt from the Form 990 read, when the page holds it. */
  instructions: Form990Instructions | null;
}

/** "Maren Lindqvist, Secretary and Treasurer": a person and their titles, as the dialog lists them. */
function withTitles(recipient: ReturnRecipient): string {
  const titles = recipient.title_labels;
  if (titles.length === 0) return recipient.display_name;
  const joined =
    titles.length === 1
      ? titles[0]
      : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
  return `${recipient.display_name}, ${joined}`;
}

export function ProvideDialog({
  orgRef,
  target,
  onClose,
}: {
  orgRef: string;
  target: ProvideTarget;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const settle = useSettleReturnAct(orgRef);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [checked, setChecked] = useState<CopyCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recipientsQuery = useReturnRecipients(orgRef, true);
  const recipients = recipientsQuery.data ?? [];
  const directors = recipients.filter((recipient) => recipient.is_director);
  const others = recipients.filter((recipient) => !recipient.is_director);
  const year = target.taxYear.named_year;

  const add = useMutation({
    mutationFn: async (file: File): Promise<CopyCheck> => {
      const path = { org_ref: orgRef };
      // 1. Where to put it: the key is minted server-side; we only echo it back.
      const issued = await operations.officerIssueBoardReturnCopyUploadUrl({
        path,
        body: { content_type: PDF },
        throwOnError: true,
      });
      if (file.size > issued.data.max_byte_size) throw new SaidError(w.tooLarge);
      // 2. The bytes go straight to storage, carrying no Givmo credential.
      const stored = await fetch(issued.data.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': issued.data.content_type ?? PDF,
          'Content-Disposition': 'attachment',
        },
        body: file,
      });
      if (!stored.ok) throw new SaidError(w.fileRefused);
      // 3. Board reads what landed, and says what it found.
      const confirmed = await operations.officerConfirmBoardReturnCopy({
        path,
        body: { object_ref: issued.data.object_ref, file_name: file.name },
        throwOnError: true,
      });
      const check = decodeCopyCheck(confirmed.data);
      if (check === null) throw new SaidError(w.fileRefused);
      return check;
    },
    onMutate: () => setError(null),
    onSuccess: (check) => setChecked(check),
    onError: (caught) => {
      setChecked(null);
      setError(
        caught instanceof SaidError ? caught.message : servedSentence(caught, w.fileRefused),
      );
    },
  });

  const provide = useMutation({
    mutationFn: async (copy: CopyCheck) => {
      const { data } = await operations.officerProvideBoardReturn({
        path: { org_ref: orgRef },
        body: {
          tax_year: { begins_on: target.taxYear.begins_on, ends_on: target.taxYear.ends_on },
          ...(target.yearEnd === null ? {} : { year_end: target.yearEnd }),
          // Board's own read of the file, echoed: the act reads it again and refuses a change.
          upload: {
            object_ref: copy.object_ref,
            generation: copy.generation,
            sha256: copy.sha256,
          },
          file_name: copy.file_name,
          // The officer's own calendar day (the act's day, §5.2 there): never a UTC slice.
          provided_on: todayAsDateInput(),
          ...(target.followsRecordId === null ? {} : { follows_record_id: target.followsRecordId }),
        },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (data) => {
      await settle(data);
      onClose();
      const recordId = (data as { record_id?: unknown } | undefined)?.record_id;
      if (typeof recordId === 'string') {
        void navigate({ to: '/form-990/returns/$recordId', params: { recordId } });
      }
    },
    onError: (caught) => setError(servedSentence(caught, w.provideFailed)),
  });

  const ready = checked !== null && recipientsQuery.isSuccess && !add.isPending;

  return (
    <DialogFrame
      title={
        target.followsRecordId === null
          ? w.provideTitle(year, target.form)
          : w.provideNewerTitle(year, target.form)
      }
      testId="return-provide-dialog"
      width={412}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="return-provide-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={!ready}
            loading={provide.isPending}
            onClick={() => {
              if (checked !== null) provide.mutate(checked);
            }}
            testId="return-provide-confirm"
          >
            {w.provideIt}
          </Button>
        </>
      }
    >
      <section className="flex flex-col gap-8">
        <PartLabel>{w.theReturnLabel}</PartLabel>
        <input
          ref={inputRef}
          type="file"
          accept={PDF}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            // Cleared, so picking the SAME file again still fires a change event.
            event.target.value = '';
            if (file !== null) add.mutate(file);
          }}
          data-testid="return-provide-input"
        />
        {checked !== null && !add.isPending ? (
          <div
            className="flex items-center gap-10 rounded-lg border border-border-subtle px-12 py-10"
            data-testid="return-provide-file"
          >
            <Icon icon={FileText} size={18} aria-hidden className="shrink-0 text-text-tertiary" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
                {checked.file_name}
              </span>
              <span className={QUIET}>
                {w.fileFacts(checked.page_count, fileSize(checked.byte_size))}
              </span>
            </span>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => inputRef.current?.click()}
              testId="return-provide-replace"
            >
              {w.replaceFile}
            </Button>
          </div>
        ) : (
          <div>
            <Button
              appearance="saas"
              tone="secondary"
              loading={add.isPending}
              onClick={() => inputRef.current?.click()}
              testId="return-provide-add"
            >
              {add.isPending ? w.addingFile : w.addFile}
            </Button>
          </div>
        )}
        {target.instructions !== null ? (
          <div>
            <WhatTheInstructionsSay
              instructions={target.instructions}
              testId="return-provide-instructions"
            />
          </div>
        ) : null}
      </section>

      <section className="flex flex-col gap-8">
        <PartLabel>{w.whoReceivesIt}</PartLabel>
        {recipientsQuery.isPending ? (
          <Skeleton
            className="h-[64px] w-full rounded-lg"
            testId="return-provide-recipients-loading"
          />
        ) : recipientsQuery.isError ? (
          <ErrorMessage testId="return-provide-recipients-error">{w.recipientsFailed}</ErrorMessage>
        ) : (
          <div className="flex flex-col gap-8" data-testid="return-provide-recipients">
            {directors.length > 0 ? (
              <p className={SECONDARY}>
                {w.yourDirectors(directors.map((recipient) => recipient.display_name))}
              </p>
            ) : null}
            {others.length > 0 ? (
              <p className={SECONDARY}>
                {directors.length > 0
                  ? w.notDirectors(others.map(withTitles))
                  : w.everyoneOnTheRoster(others.map(withTitles))}
              </p>
            ) : null}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-8">
        <PartLabel>{w.whatTheyGet}</PartLabel>
        <p className={SECONDARY}>{w.whatTheyGetBody(year, target.form)}</p>
      </section>

      <p className={QUIET}>{w.keptNote}</p>

      {error !== null ? <ErrorMessage testId="return-provide-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}
